import { CustomError } from 'ts-custom-error';
import * as R from 'ramda';
import { RawErrorStatus } from './types/RawErrorStatus';
import { isString } from '../../../helper/string';
/**
 * Custom error class for API call errors.
 * Has a `type` property that classifies the error by HTTP status or timeout, etc.
 * Has information about the request: endpoint, method, and request body.
 * If built from a HTTP `Response` object, then potential JSON can be accessed.
 */
export class APIError<Body = unknown> extends CustomError {
  static readonly STATUS_TIMEOUT = -1;
  static readonly STATUS_NETWORK_ERROR = -2;
  static readonly STATUS_MISSING_TOKENS_ERROR = -3;

  static readonly STATUS_UNAUTHORIZED = 401;
  static readonly STATUS_GONE = 410; // Device or account removed

  static readonly ERROR_TIMEOUT = 'timeout';
  static readonly ERROR_CLIENT = 'client';
  static readonly ERROR_SERVER = 'server';
  static readonly ERROR_UNKNOWN = 'unknown';
  static readonly ERROR_UNAUTHORIZED = 'unauthorized';
  static readonly ERROR_NETWORK = 'network';

  status: number;
  statusText: string;
  url: string;
  endpoint: string;
  method: string;
  requestBody: Body;

  constructor(
    response: Response | RawErrorStatus,
    endpoint: string,
    method: string,
    requestBody: Body
  ) {
    if (response instanceof Response) {
      super(
        `QApi Error in request: ${method} ${endpoint} ${response.status} ${response.statusText}`
      );
      this.url = response.url;
      this.json = R.once(() => response.json());
    } else {
      super(
        `QApi Error in request: ${method} ${response.status} ${response.statusText}`
      );
      this.url = endpoint;
      this.json = R.once(() =>
        Promise.reject(new Error('no json due to fetch error'))
      );
    }

    // Set name explicitly as minification can mangle class names
    Object.defineProperty(this, 'name', { value: 'APIError' });
    this.status = response.status;
    this.statusText = response.statusText;
    this.endpoint = endpoint;
    this.method = method;
    this.requestBody = requestBody;
  }

  get type() {
    if (this.status === APIError.STATUS_TIMEOUT) {
      return APIError.ERROR_TIMEOUT;
    }
    if (this.status === APIError.STATUS_UNAUTHORIZED) {
      return APIError.ERROR_UNAUTHORIZED;
    }
    if (this.status >= 400 && this.status < 500) {
      return APIError.ERROR_CLIENT;
    }
    if (this.status >= 500) {
      return APIError.ERROR_SERVER;
    }
    if (this.status === APIError.STATUS_NETWORK_ERROR) {
      return APIError.ERROR_NETWORK;
    }
    return APIError.ERROR_UNKNOWN;
  }

  /**
   * Return promise with message of `{error: "message"}` in response, or empty
   * string, or string of internal error if applicable.
   */
  async errorMessage(): Promise<string | undefined> {
    try {
      const json = await this.json();
      if (!json?.error || !isString(json.error)) {
        return undefined;
      }
      return json.error;
    } catch (error) {
      return error instanceof Error ? error.message : undefined;
    }
  }

  /**
   * Returns the response json() promise. Unlike `Response.json()`, it can be
   * called multiple times.
   */
  json: <T = Record<string, string>>() => Promise<T>;

  static isUnauthorizedAuthorization(error: APIError | unknown) {
    return (
      error instanceof APIError &&
      error.status === APIError.STATUS_UNAUTHORIZED &&
      /oauth2\/access_token$/.test(error.url)
    );
  }

  static isMissingAuthTokens(error: APIError | Error) {
    return (
      error instanceof APIError &&
      error.status === APIError.STATUS_MISSING_TOKENS_ERROR
    );
  }
}
